<template>
  <div class="page-container">
    <PageHeader title="项目管理" show-breadcrumb />

    <SearchPanel>
      <el-input v-model.trim="query.zapName" placeholder="项目名称" clearable size="small" @keyup.enter.native="handleSearch" />
      <template #actions>
        <el-button type="primary" icon="el-icon-search" size="small" @click="handleSearch">查询</el-button>
        <el-button icon="el-icon-refresh" size="small" @click="handleReset">重置</el-button>
        <el-button v-if="isAdmin" type="success" icon="el-icon-plus" size="small" @click="handleAdd">新增项目</el-button>
      </template>
    </SearchPanel>

    <TablePanel title="项目列表">
      <el-table :data="list" v-loading="loading" size="small" stripe :height="tableHeight">
        <el-table-column type="index" label="序号" width="80" align="center" />
        <el-table-column prop="zapName" label="项目名称" min-width="240" align="center" show-overflow-tooltip />
        <el-table-column prop="zapDateStart" label="开始日期" min-width="140" align="center" />
        <el-table-column prop="zapDateEnd" label="结束日期" min-width="140" align="center" />
        <el-table-column v-if="isAdmin" label="操作" width="180" align="center">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" style="color:#409eff" @click="handleEdit(row)">编辑</el-button>
            <el-button type="text" size="mini" style="color:#e6a23c" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        :current-page="page.currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="page.pageSize"
        :total="page.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      />
    </TablePanel>

    <!-- 新增/编辑项目弹框（仅系统管理员可操作） -->
    <el-dialog :title="dialogType === 'add' ? '新增项目' : '编辑项目'" :visible.sync="dialogVisible" width="520px" v-dialog-drag-resize>
      <el-form ref="formRef" :model="form" :rules="rules" label-width="100px" size="small">
        <el-form-item label="项目名称" prop="zapName">
          <el-input v-model.trim="form.zapName" placeholder="请输入项目名称" />
        </el-form-item>
        <el-form-item label="开始日期" prop="zapDateStart">
          <el-date-picker v-model="form.zapDateStart" type="date" format="yyyy-MM-dd" value-format="yyyy-MM-dd" placeholder="请选择开始日期" style="width:100%" />
        </el-form-item>
        <el-form-item label="结束日期" prop="zapDateEnd">
          <el-date-picker v-model="form.zapDateEnd" type="date" format="yyyy-MM-dd" value-format="yyyy-MM-dd" placeholder="请选择结束日期" style="width:100%" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="submitLoading" @click="submitDialog">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import PageHeader from '@/components/PageHeader.vue'
import SearchPanel from '@/components/SearchPanel.vue'
import TablePanel from '@/components/TablePanel.vue'
import { getZqpkAuthProjectList, addZqpkAuthProject, updateZqpkAuthProject, deleteZqpkAuthProject } from '@/api/api'

export default {
  name: 'AuthProject',
  components: { PageHeader, SearchPanel, TablePanel },
  data () {
    return {
      loading: false,
      tableHeight: 400,
      list: [],
      query: { zapName: '' },
      page: { currentPage: 1, pageSize: 10, total: 0 },
      dialogVisible: false,
      dialogType: 'add',
      submitLoading: false,
      form: { zapId: null, zapName: '', zapDateStart: '', zapDateEnd: '' },
      rules: {
        zapName: [{ required: true, message: '请输入项目名称', trigger: 'blur' }]
      }
    }
  },
  computed: {
    // 仅系统管理员（zauPost===1）可增删改项目
    isAdmin () {
      const u = this.$store.state.user.userInfo || {}
      return u.zauPost === 1
    }
  },
  created () { this.fetchList() },
  mounted () {
    this.$nextTick(this.calcTableHeight)
    window.addEventListener('resize', this.calcTableHeight)
  },
  beforeDestroy () { window.removeEventListener('resize', this.calcTableHeight) },
  methods: {
    calcTableHeight () {
      const el = this.$el.querySelector('.el-table')
      if (!el) return
      this.tableHeight = window.innerHeight - el.getBoundingClientRect().top - 80
    },
    async fetchList () {
      this.loading = true
      try {
        const params = { ...this.query, currentPage: this.page.currentPage, pageSize: this.page.pageSize }
        const res = await getZqpkAuthProjectList(params)
        this.list = res.data || []
        this.page.total = res.total || 0
      } catch (e) { this.list = []; this.page.total = 0 } finally { this.loading = false }
    },
    handleSearch () { this.page.currentPage = 1; this.fetchList() },
    handleReset () { this.query = { zapName: '' }; this.handleSearch() },
    handleSizeChange (size) { this.page.pageSize = size; this.handleSearch() },
    handlePageChange (page) { this.page.currentPage = page; this.fetchList() },
    handleAdd () {
      this.dialogType = 'add'
      this.form = { zapId: null, zapName: '', zapDateStart: '', zapDateEnd: '' }
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
    },
    handleEdit (row) {
      this.dialogType = 'edit'
      this.form = { zapId: row.zapId, zapName: row.zapName || '', zapDateStart: row.zapDateStart || '', zapDateEnd: row.zapDateEnd || '' }
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
    },
    submitDialog () {
      this.$refs.formRef.validate(async valid => {
        if (!valid) return
        this.submitLoading = true
        try {
          if (this.dialogType === 'add') {
            await addZqpkAuthProject(this.form)
            this.$message.success('新增成功')
          } else {
            await updateZqpkAuthProject(this.form)
            this.$message.success('编辑成功')
          }
          this.dialogVisible = false
          this.fetchList()
        } catch (e) {} finally {
          this.submitLoading = false
        }
      })
    },
    async handleDelete (row) {
      try { await this.$confirm('确定删除该项目？', '提示', { type: 'warning' }) } catch { return }
      try {
        await deleteZqpkAuthProject({ zapId: row.zapId })
        this.$message.success('删除成功')
        this.fetchList()
      } catch (e) {}
    }
  }
}
</script>
